import type { TFuncKey, TFunction } from 'shared/translations'
import { type ThemeColor } from '@precis-digital/kurama/theme'
import {
  type RechartsContentType as ContentType,
  type RechartsBarProps as BarProps,
  type RechartsLineProps as LineProps,
  type RechartsAreaProps as AreaProps,
  type RechartsPieProps as PieProps,
  type RechartsScatterProps as ScatterProps,
  type RechartsXAxisProps as XAxisProps,
  type RechartsYAxisProps as YAxisProps,
  type RechartsBarChartProps,
  type RechartsLineChartProps,
  type RechartsAreaChartProps,
  type RechartsScatterChartProps,
  type RechartsComposedChartProps,
  type RechartsPieChartProps
} from '@precis-digital/kurama/recharts'
import type { ColumnDef, ColumnPinningPosition, Row, RowData, SortDirection, SortingState } from '@tanstack/react-table'
import type { ComponentType, ReactElement, SVGProps } from 'react'
import type { FormatDateToStringProps } from 'shared/dateFns'
import type { FormatToMoneyProps, FormatToPercentProps } from 'shared/numberFormat'
import type {
  SelectionValuesMap,
  FontProps,
  XYAxisProps,
  TableSection,
  TableColumnValuesChoiceType,
  TableColumnsChoiceType,
  OthersIDSuffixType,
  TotalsAndRangeData
} from './internalTypes'
import type { CellAlignment, ColumnColorScaleState } from './components/table/types'
import type { ChartCardTableColumnDef } from './components/table/tanstack'

export type { ChartColorName } from './constants'

export type RequiredAllowUndefined<T> = {
  [K in keyof Required<T>]: T[K] | undefined
}

/**
 * ScatterChartType is the type used to identify a scatter chart.
 */
export type ScatterChartType = 'scatter'

/**
 * CartesianChartType is the type used to identify cartesian chart kinds (bar, line, area).
 */
export type CartesianChartType = 'bar' | 'line' | 'gradientLine' | 'area'

/**
 * CartesianChartType is the type used to identify cartesian chart kinds (bar, line, area).
 */
export type PerformanceChartType = 'line' | 'gradientLine' | 'area'

/**
 * ComposableChartTypes is the type used to identify chart kinds that can be mixed in a composed chart (bar, line).
 */
export type ComposableChartTypes = 'bar' | 'line'

/**
 * PolarChartType is the type used to identify polar chart kinds (pie, donut).
 */
export type PolarChartType = 'pie' | 'donut'

/**
 * StandardChartType is the union of cartesian and polar chart types.
 */
export type StandardChartType = CartesianChartType | PolarChartType

/**
 * ViewType is the type used to define the chart view (cartesian or polar or table).
 */
export type ViewType = 'cartesian' | 'polar' | Exclude<ChartCardType, 'chart'>

/**
 * LayoutType is the type used to define the chart layout orientation.
 */
export type LayoutType = 'horizontal' | 'vertical'

/**
 * DrawingColorKey is the type used for SVG color-related props (fill, stroke, color).
 */
export type DrawingColorKey = keyof Pick<SVGProps<SVGElement>, 'fill' | 'stroke' | 'color'>

/**
 * ChartType is the type used to identify any supported chart kind (scatter, bar, line, area, pie, donut).
 */
export type ChartType = ScatterChartType | StandardChartType

/**
 * ChartType is the type used to identify any supported chart kind (scatter, bar, line, area, pie, donut).
 */
export type SpecialDrawingChartType = Extract<ChartType, 'gradientLine'>

/**
 * RechartsChartType extends ChartType with 'composed' for charts that combine multiple (cartesian) chart types in the same chart.
 */
export type RechartsChartType = ChartType | 'composed'

/**
 * LinePattern is the type used to define the line pattern for line charts (continuous, dot, dash, etc.).
 */
export type LinePattern = 'continuous' | 'dot' | 'dash' | 'line' | 'line-dot' | 'dash-dot'

/**
 * ChartCardGridSize is the type used to define the size of a chart card in the grid.
 */
export type ChartCardGridSize = 'small' | 'medium' | 'large' | 'full'

/**
 * MetricAggregation is the type used to define how metric values are aggregated (sum, average, min, max, count, any).
 */
export type MetricAggregation = 'sum' | 'average' | 'min' | 'max' | 'count' | 'any'

/**
 * ExtendedDataKey is a type that extends DataKey with choice-related keys (e.g. with OthersIDSuffix or nested keys).
 */
export type ExtendedDataKey<DataKey extends string, ChoicesKey extends string> =
  | DataKey
  | ChoicesKey
  | `${ChoicesKey}${OthersIDSuffixType}`
  | `${ChoicesKey}.${ChoicesKey}`
  | `${ChoicesKey}.${ChoicesKey}${OthersIDSuffixType}`

/**
 * DataKeyMap is a utility type for DataKey maps.
 *
 * It is defined as Partial, to force proper checks for missing values in the maps.
 */
export type DataKeyMap<DataKey extends string, Value> = Partial<Record<DataKey, Value>>

/**
 * ChartDataValue is the type used to define the value of a data row field.
 *
 * It is a string | number | Date | null.
 */
export type ChartDataValue = string | number | boolean | null

/**
 * ChartDataRow is the type used to define the structure of the data row.
 *
 * * It is a map of DataKey to string | number | boolean | Date | null.
 * * It is a DataKeyMap (partial map)as the data structure is not verified against the data definition
 * (metrics and dimensions), so this forces proper nullish checks when handling the data.
 */
export type ChartDataRow<DataKey extends string> = DataKeyMap<DataKey, ChartDataValue>

/**
 * ChartDataRowSort is the type used for a custom sort function that sorts chart data rows.
 *
 * @param axisId - The axis id.
 * @param choiceId - The choice id.
 * @param dimension - The dimension key.
 * @param metrics - The metrics keys.
 * @param data - The data rows to sort. Type: {@link ChartDataRow}[]
 */
export type ChartDataRowSort<DataKey extends string, DataRow extends ChartDataRow<DataKey>> = (
  axisId: DataKey,
  choiceId: DataKey,
  dimension: DataKey,
  metrics: DataKey[],
  data: DataRow[]
) => DataRow[]

/**
 * ChartFilterValues is the type used for a custom filter function that filters chart data rows.
 *
 * @param axisId - The axis id.
 * @param choiceId - The choice id.
 * @param dimension - The dimension key.
 * @param metrics - The metrics keys.
 * @param data - The data rows to filter. Type: {@link ChartDataRow}[]
 */
export type ChartFilterValues<DataKey extends string, DataRow extends ChartDataRow<DataKey>> = (
  axisId: DataKey,
  choiceId: DataKey,
  dimension: DataKey,
  metrics: DataKey[],
  data: DataRow[]
) => DataRow[]

/**
 * MetricFilterType is the one of the possible data rows filter choices.
 *
 * It's the kind used to filter the data rows to only include rows with valued metrics.
 */
export type MetricFilterType = 'valuedMetrics'

/**
 * ChartOnSelectionsUpdated is the signature of the callback invoked when chart selections change.
 *
 * @param selections - The current chart selections. Type: {@link ChartSelections}
 * @param id - The chart card id.
 */
export type ChartOnSelectionsUpdated<DataKey extends string> = (
  selections: ChartSelections<DataKey>,
  id: string
) => void

/**
 * VariantNewMetrics is the type used to describe the new (virtual) metrics in a metrics variants dimension.
 */
export type VariantNewMetrics<DataKey extends string> = DataKeyMap<DataKey, BaseElementDescription>

/**
 * VariantVariantsMap is the type used to map variants (the keys) to the mapping between the new metrics and the real metrics.
 */
export type VariantVariantsMap<DataKey extends string> = Record<string, DataKeyMap<DataKey, DataKey>>

/**
 * AlertType is the type used to define the severity of an alert (info, warning, error).
 */
export type AlertType = 'info' | 'warning' | 'error'

/**
 * BaseElementDescription is the interface used to describe a chart element (label, description, color, pattern, alert).
 *
 * @property label - Optional display label.
 * @property description - Optional longer description.
 * @property color - Optional theme color.
 * @property pattern - Optional line pattern. Type: {@link LinePattern}
 * @property alert - Optional alert severity. Type: {@link AlertType}
 * @property alertText - Optional alert message.
 */
export interface BaseElementDescription {
  label?: string
  description?: string
  color?: ThemeColor
  pattern?: LinePattern
  alert?: AlertType
  alertText?: string
  icon?: ReactElement
  assetUrl?: string
}

/**
 * ChartChoiceOption is the type used to describe an option (either a metric, dimension or dimension value).
 *
 * @property value - The option value.
 * @property values - Optional set of option values.
 * Extends {@link BaseElementDescription}.
 */
export interface ChartChoiceOption<Option extends string> extends BaseElementDescription {
  value: Option
  values?: Set<Option>
  disabled?: boolean
}

/**
 * RequestChange is the signature for the callback invoked to validate a selection change (hybrid).
 * Should return the new selection or null/undefined if the change is rejected.
 *
 * @param selected - The new selection.
 * @param id - The id of the variable.
 * @param selectionOf - The selection of the other variable (valueOf or valuesOf).
 */
export type RequestChange<DataKey extends string, Selected extends string | string[]> = (
  selected: Selected,
  id: DataKey,
  selectionOf?: string
) => Selected | null | undefined

/**
 * OnChange is the signature for the callback invoked when a variable selection changes (internal).
 *
 * @param selected - The new selection.
 * @param id - The id of the variable.
 * @param selectionOf - The selection of the other variable (valueOf or valuesOf).
 */
export type OnChange<DataKey extends string, Selected extends string | string[]> = (
  selected: Selected,
  id: DataKey,
  selectionOf?: string
) => void

/**
 * OptionsCallback is the type used for a function that returns available options for a variable from data.
 *
 * @param data - The data rows. Type: {@link ChartDataRow}[]
 * @param id - The variable id.
 * @param optionsOf - When OptionsOf is true, the dimension key for which options are computed.
 */
export type OptionsCallback<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Option extends string,
  OptionsOf extends boolean
> = OptionsOf extends true
  ? (data: DataRow[], id: DataKey, optionsOf: DataKey) => Option[]
  : (data: DataRow[], id: DataKey) => Option[]

/**
 * DescriptionsMapCallback is the type used for a function that returns descriptions (label, color, etc.) for dimension values from data.
 *
 * @param id - The dimension id.
 * @param data - The data rows. Type: {@link ChartDataRow}[]
 * Returns `Record<string, {@link BaseElementDescription}>`.
 */
export type DescriptionsMapCallback<DataKey extends string, DataRow extends ChartDataRow<DataKey>> = (
  id: DataKey,
  data: DataRow[]
) => Record<string, BaseElementDescription>

/**
 * DimensionDescriptionsMapType is the type used for a static map of dimension value keys to their descriptions.
 */
export type DimensionDescriptionsMapType = Record<string, BaseElementDescription>

/**
 * DimensionDescriptionsMapTypes is the type used for dimension descriptions: either a static map or a callback that builds it from data.
 */
export type DimensionDescriptionsMapTypes<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = Space extends 'choice'
  ? DimensionDescriptionsMapType
  : DimensionDescriptionsMapType | DescriptionsMapCallback<DataKey, DataRow>

/**
 * BaseDataElement is the interface used as the base for dimensions and metrics, adding an id to BaseElementDescription.
 *
 * @property id - The data key identifying this dimension or metric.
 * Extends {@link BaseElementDescription}.
 */
export interface BaseDataElement<DataKey extends string> extends BaseElementDescription {
  id: DataKey
}

/**
 * MetricFormulaCallback is the type used to define a formula callback for a metric.
 *
 * @param id - The data key.
 * @param row - The data row.
 * @returns The result value or null.
 */
export type MetricFormulaCallback<DataKey extends string, DataRow extends ChartDataRow<DataKey>> = (
  id: DataKey,
  row: DataRow
) => number | null

/**
 * MetricFormatCallback is the type used to define a format callback for a metric.
 *
 * @param id - The data key.
 * @param value - The value to format.
 * @returns The formatted value.
 */
export type MetricFormatCallback<DataKey extends string> = (id: DataKey, value: number | null) => string

/**
 * BaseDataMetric is the interface used as the base for metric definitions (aggregation, missing value).
 *
 * @property formula - The formula to use to calculate the metric.
 * @property recalculate - Whether to recalculate the metric from aggregated formula dependencies
 *   (scorecard headlines, table footers) instead of aggregating precomputed row values.
 * @property aggregation - How values are aggregated (sum, average, min, max, count, any). Type: {@link MetricAggregation}
 * @property missingValue - Value to use when data is missing.
 * Extends {@link BaseDataElement}.
 */
interface BaseDataMetric<DataKey extends string, DataRow extends ChartDataRow<DataKey>>
  extends BaseDataElement<DataKey> {
  formula?: string | MetricFormulaCallback<DataKey, DataRow>
  recalculate?: boolean
  aggregation?: MetricAggregation
  missingValue?: number | null
}

/**
 * MetricCurrency is the interface used to define a currency metric (format, currencyCode).
 *
 * @property type - The metric type ('currency').
 * @property format - How to format the value for display. Type: `Omit<{@link FormatToMoneyProps}, 'value'>` or callback.
 * @property currencyCode - Optional currency code.
 * Extends {@link BaseDataMetric}.
 */
interface MetricCurrency<DataKey extends string, DataRow extends ChartDataRow<DataKey>>
  extends BaseDataMetric<DataKey, DataRow> {
  type: 'currency'
  format?: Omit<FormatToMoneyProps, 'value'> | MetricFormatCallback<DataKey>
  currencyCode?: string
}

/**
 * Number format options: every {@link Intl.NumberFormatOptions} field plus `compact`.
 *
 * @property compact - Shorthand for `notation: 'compact'` with `compactDisplay: 'short'`. Optional. Default: true.
 * Explicit Intl options override the `compact` shorthand.
 */
export interface FormatToNumberProps extends Intl.NumberFormatOptions {
  compact?: boolean
}

/**
 * MetricNumber is the interface used to define a numeric metric (format).
 *
 * @property type - The metric type ('number').
 * @property format - How to format the value for display. Type: {@link FormatToNumberProps} or callback.
 * Extends {@link BaseDataMetric}.
 */
interface MetricNumber<DataKey extends string, DataRow extends ChartDataRow<DataKey>>
  extends BaseDataMetric<DataKey, DataRow> {
  type: 'number'
  format?: FormatToNumberProps | MetricFormatCallback<DataKey>
}

/**
 * MetricPercentage is the interface used to define a percentage metric (format).
 *
 * @property type - The metric type ('percentage').
 * @property format - How to format the value for display. Type: {@link FormatToPercentProps} or callback.
 * Extends {@link BaseDataMetric}.
 */
interface MetricPercentage<DataKey extends string, DataRow extends ChartDataRow<DataKey>>
  extends BaseDataMetric<DataKey, DataRow> {
  type: 'percentage'
  format?: FormatToPercentProps | MetricFormatCallback<DataKey>
}

/**
 * Metric is the type used to define a metric in the data definition (currency, number, or percentage).
 */
export type Metric<DataKey extends string = string, DataRow extends ChartDataRow<DataKey> = ChartDataRow<DataKey>> =
  | MetricCurrency<DataKey, DataRow>
  | MetricNumber<DataKey, DataRow>
  | MetricPercentage<DataKey, DataRow>

export type ChartCardMetricType = Metric['type']

/**
 * DimensionDate is the interface used to define a date dimension.
 *
 * @property type - The dimension type ('date').
 * @property format - How to format the date for display (string, {@link FormatDateToStringProps}, or callback).
 * Extends {@link BaseDataElement}.
 */
export interface DimensionDate<DataKey extends string> extends BaseDataElement<DataKey> {
  type: 'date'
  format?: string | FormatDateToStringProps | ((id: DataKey, value: ChartDataValue | Date | undefined) => string | null)
  descriptions?: never
  othersDescription?: never
}

/**
 * DimensionString is the type used to define a string dimension.
 * @property type - The type of dimension (string).
 * @property format - The format of the dimension.
 * @property descriptions - The description of the dimension values. Type: {@link DimensionDescriptionsMapTypes}
 * Describes (label, description, etc.) the values of the dimension.
 * @property othersDescription - The description for all the values not defined in the descriptions map. Type: {@link ChartChoiceOption}
 * The values wil be grouped in aggregation of the data.
 * @property filter - Whether to filter the data based on this dimension.
 * * If it is a boolean, it means that the filter is based on the descriptions map, which must be provided.
 * * If it is an array, it means that only the values in the array should be included.
 * @property align - Table column alignment. Optional. Defaults to left.
 * Extends {@link BaseDataElement}.
 */
export interface DimensionString<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> extends BaseDataElement<DataKey> {
  type: 'string'
  format?: (id: DataKey, value: ChartDataValue | Date | undefined) => string | null
  defaultValue?: string
  descriptions?: DimensionDescriptionsMapTypes<Space, DataKey, DataRow>
  othersDescription?: ChartChoiceOption<string>
  filter?: string[] | true
  align?: CellAlignment
}

/**
 * DimensionStringOrDate is the type used for a dimension that is either a string or a date dimension.
 */
export type DimensionStringOrDate<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = DimensionString<Space, DataKey, DataRow> | DimensionDate<DataKey>

/**
 * MetricsVariants is a type that is used to define a "virtual" dimension based on differently calculated metrics.
 *
 * The data structure describes new metrics (newMetrics)
 * that logically represent multiple metrics have been calculated in different ways.
 * The keys of variantsMap represent the different ways,
 * and for each way the mapping between the new metrics and the real metrics.
 *
 * @property type - The type of dimension (variant).
 * @property newMetrics - Description of the new metrics (label, description, etc.). Type: {@link VariantNewMetrics}
 * @property variantsMap - Mapping between the variant values and the metrics. Type: {@link VariantVariantsMap}
 * > Each value must have a mapping for each of the metrics in newMetrics.
 * Extends {@link DimensionString} (omitting type).
 */
export interface MetricsVariants<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> extends Omit<DimensionString<Space, DataKey, DataRow>, 'type'> {
  type: 'variant'
  newMetrics: VariantNewMetrics<DataKey>
  variantsMap: VariantVariantsMap<DataKey>
  othersDescription?: never
  filter?: never
}

/**
 * Dimension is the type used to define the various types of dimension.
 *
 * A Variant is considered a Dimension, as it can be considered a dimension
 * whose values have been used to transpose metrics.
 */
export type Dimension<
  Space extends 'choice' | 'props' = 'props',
  DataKey extends string = string,
  DataRow extends ChartDataRow<DataKey> = ChartDataRow<DataKey>
> = DimensionDate<DataKey> | DimensionString<Space, DataKey, DataRow> | MetricsVariants<Space, DataKey, DataRow>

export type ChartCardDimensionType = Dimension['type']

/**
 * DataDefinition is the type used to define the structure of the data.
 *
 * @property dimensions - The dimensions of the data. Type: {@link Dimension}[]
 * @property metrics - The metrics of the data. Type: {@link Metric}[]
 */
export interface DataDefinition<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  dimensions: Array<Dimension<'props', DataKey, DataRow>>
  metrics: Array<Metric<DataKey>>
}

/**
 * ChartChoiceOptions is the type used for an array of choice options (e.g. for a dropdown).
 */
export type ChartChoiceOptions<Option extends string> = Array<ChartChoiceOption<Option>>

/**
 * ChartChoiceOptionsOf is the type used for a map from DataKey to chart choice options (for variable dimensions).
 */
export type ChartChoiceOptionsOf<DataKey extends string, Option extends string> = Record<
  DataKey,
  ChartChoiceOptions<Option>
>

/**
 * VariableOptionsPropertyType is the type used to define the options for a variable.
 *
 * @template Space - 'choice' or 'props'.
 * @template DataKey - The key of the data.
 * @template DataRow - The row of the data.
 * @template Option - The type of the option.
 * @template Options - The type of the options.
 * * 'static' - The options is an array of values. Finalised options (Space = 'choice' are static)
 * * 'optionalStatic' - The options is 'all' (all the values from the data) or an array of values
 * * 'withCallback' - The options is an array of values or a callback to get the options at run time. Only for Dimension Values.
 * * 'optionalWithCallback' - The options is 'all' (all the values from the data) or an array of values
 * or a callback to get the options at run time. Only for Dimension Values.
 * @template OptionsOf - The dimension variable type. Only for Dimension Values.
 * * 'fixed' - The dimension is fixed.
 * * 'variable' - The dimension is variable.
 * * 'na' - N/A
 */
export type VariableOptionsPropertyType<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Option extends string,
  Options extends 'static' | 'optionalStatic' | 'withCallback' | 'optionalWithCallback',
  OptionsOf extends 'fixed' | 'variable' | 'na' = 'fixed'
> = OptionsOf extends 'variable'
  ? // dimension value options related to a variable dimension
    Space extends 'choice'
    ? // Used for internal choices. Options are finalised ('all' / callback are handled) and fully described.
      ChartChoiceOptionsOf<DataKey, Option>
    : Options extends 'static'
    ? // Options are static
      Record<DataKey, Option[]>
    : Options extends 'optionalStatic'
    ? // Options are optional (all) or static
      'all' | Record<DataKey, 'all' | Option[]>
    : Options extends 'withCallback'
    ? // Options are static or a callback
      Record<DataKey, Option[]> | OptionsCallback<DataKey, DataRow, Option, true>
    : Options extends 'optionalWithCallback'
    ? // Options are optional or static or a callback
      'all' | Record<DataKey, 'all' | Option[]> | OptionsCallback<DataKey, DataRow, Option, true>
    : // Invalid
      never
  : // Options are not dimension values or the dimension is static
  Space extends 'choice'
  ? // Used for internal choices. Options are finalised ('all' / callback are handled) and fully described.
    ChartChoiceOptions<Option>
  : Options extends 'static'
  ? // Options are static
    Option[]
  : Options extends 'optionalStatic'
  ? // Options are optional (all) or static
    'all' | Option[]
  : Options extends 'withCallback'
  ? // Options are static or a callback
    Option[] | OptionsCallback<DataKey, DataRow, Option, false>
  : Options extends 'optionalWithCallback'
  ? // Options are optional or static or a callback
    'all' | Option[] | OptionsCallback<DataKey, DataRow, Option, false>
  : // Invalid
    never

/**
 * ChartBaseVariable is the type used to define the description of a chart variable.
 *
 * @property id - The id of the variable. It cannot be the same as a dimension or metric id.
 * @property type - The type of variable.
 * @property label - Optional label of the variable.
 * @property description - Optional longer description of the variable.
 * @property color - Optional theme color.
 * @property pattern - Optional line pattern. Type: {@link LinePattern}
 */
interface ChartBaseVariable<DataKey extends string> {
  id: DataKey
  type:
    | 'dimension'
    | 'metric'
    | 'metrics'
    | 'metricVariants'
    | 'metricTransposed'
    | 'value'
    | 'variantValue'
    | 'valueOf'
    | 'values'
    | 'variantValues'
    | 'valuesOf'
    | TableColumnsChoiceType
    | TableColumnValuesChoiceType
  label?: string
  description?: string
  color?: ThemeColor
  pattern?: LinePattern
}

export type ChangeType = 'static' | 'controlled' | 'internal' | 'hybrid'

/**
 * ChangeBase is the interface used as the base for variable change behaviour (static, controlled, internal, hybrid).
 *
 * @property change - The change mode: {@link ChangeType}.
 * @property onChange - Callback when selection changes (internal/controlled). Type: {@link OnChange}
 * @property requestChange - Callback to validate a selection change (hybrid). Type: {@link RequestChange}
 * @property display - Whether to show the variable in the ChartCard UI.
 * @property label - Optional label.
 * @property description - Optional description.
 */
export interface ChangeBase<DataKey extends string, Option extends string | string[]> {
  change: ChangeType
  onChange?: OnChange<DataKey, Option>
  requestChange?: RequestChange<DataKey, Option>
  display?: boolean
  label?: string
  description?: string
}

/**
 * ChangeStatic is the type used to define a static variable.
 *
 * Only for choice space.
 *
 * @property change - The type of change, static.
 * @property options - The options for the variable. Type: {@link VariableOptionsPropertyType}
 * @property otherOption - When OptionsOf is not 'na', the option used for "others".
 * Extends {@link ChangeBase}.
 */
interface ChangeStatic<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Option extends string,
  Options extends 'static' | 'optionalStatic' | 'withCallback' | 'optionalWithCallback',
  OptionsOf extends 'fixed' | 'variable' | 'na'
> extends ChangeBase<DataKey, Option> {
  change: 'static'
  options: VariableOptionsPropertyType<Space, DataKey, DataRow, Option, Options, OptionsOf>
  otherOption?: OptionsOf extends 'na' ? Option : never
  onChange?: never
  requestChange?: never
  display?: never
  label?: never
  description?: never
}

/**
 * ChangeControlled is the type used to define a controlled variable.
 *
 * @property change - The type of change, controlled.
 * @property options - The options for the variable. Type: {@link VariableOptionsPropertyType}
 * @property otherOption - When OptionsOf is not 'na', the option used for "others".
 * @property display - Whether the value should be displayed in the ChartCard.
 * Extends {@link ChangeBase}.
 */
interface ChangeControlled<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Option extends string,
  Options extends 'static' | 'optionalStatic' | 'withCallback' | 'optionalWithCallback',
  OptionsOf extends 'fixed' | 'variable' | 'na'
> extends ChangeBase<DataKey, Option> {
  change: 'controlled'
  options: VariableOptionsPropertyType<Space, DataKey, DataRow, Option, Options, OptionsOf>
  otherOption?: OptionsOf extends 'na' ? Option : never
  onChange?: never
  requestChange?: never
  display?: boolean
}

/**
 * ChangeSingleInternal is the type used to define a single select internal variable.
 *
 * @property change - The type of change, internal.
 * @property options - The options for the variable. Type: {@link VariableOptionsPropertyType}
 * @property otherOption - When OptionsOf is not 'na', the option used for "others".
 * @property onChange - The function to call when the value changes. Type: {@link OnChange}
 * Extends {@link ChangeBase}.
 */
interface ChangeSingleInternal<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Option extends string,
  Options extends 'static' | 'optionalStatic' | 'withCallback' | 'optionalWithCallback',
  OptionsOf extends 'fixed' | 'variable' | 'na'
> extends ChangeBase<DataKey, Option> {
  change: 'internal'
  options: VariableOptionsPropertyType<Space, DataKey, DataRow, Option, Options, OptionsOf>
  otherOption?: OptionsOf extends 'na' ? Option : never
  onChange?: OnChange<DataKey, Option>
  requestChange?: never
  display?: never
}

/**
 * ChangeSingleHybrid is the type used to define a single select hybrid variable.
 *
 * The requestChange function must return the selected option or null/undefined if the change is not allowed.
 *
 * @property change - The type of change, hybrid.
 * @property options - The options for the variable. Type: {@link VariableOptionsPropertyType}
 * @property otherOption - When OptionsOf is not 'na', the option used for "others".
 * @property requestChange - The function to call to validate the value change. Type: {@link RequestChange}
 * Extends {@link ChangeBase}.
 */
interface ChangeSingleHybrid<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Option extends string,
  Options extends 'static' | 'optionalStatic' | 'withCallback' | 'optionalWithCallback',
  OptionsOf extends 'fixed' | 'variable' | 'na'
> extends ChangeBase<DataKey, Option> {
  change: 'hybrid'
  options: VariableOptionsPropertyType<Space, DataKey, DataRow, Option, Options, OptionsOf>
  otherOption?: OptionsOf extends 'na' ? Option : never
  onChange?: never
  requestChange: RequestChange<DataKey, Option>
  display?: never
}

/**
 * ChangeMultipleInternal is the type used to define a multiple select internal variable.
 *
 * @property change - The type of change, internal.
 * @property options - The options for the variable. Type: {@link VariableOptionsPropertyType}
 * @property otherOption - When OptionsOf is not 'na', the option used for "others".
 * @property onChange - The function to call when the value changes. Type: {@link OnChange}
 * Extends {@link ChangeBase}.
 */
interface ChangeMultipleInternal<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Option extends string,
  Options extends 'static' | 'optionalStatic' | 'withCallback' | 'optionalWithCallback',
  OptionsOf extends 'fixed' | 'variable' | 'na'
> extends ChangeBase<DataKey, Option[]> {
  change: 'internal'
  options: VariableOptionsPropertyType<Space, DataKey, DataRow, Option, Options, OptionsOf>
  otherOption?: OptionsOf extends 'na' ? Option : never
  onChange?: OnChange<DataKey, Option[]>
  requestChange?: never
  display?: never
}

/**
 * ChangeMultipleHybrid is the type used to define a multiple select hybrid variable.
 *
 * The requestChange function should return the selected options or null if the change is not allowed.
 *
 * @property change - The type of change, hybrid.
 * @property options - The options for the variable. Type: {@link VariableOptionsPropertyType}
 * @property otherOption - When OptionsOf is not 'na', the option used for "others".
 * @property requestChange - The function to call to validate the value change. Type: {@link RequestChange}
 * Extends {@link ChangeBase}.
 */
interface ChangeMultipleHybrid<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Option extends string,
  Options extends 'static' | 'optionalStatic' | 'withCallback' | 'optionalWithCallback',
  OptionsOf extends 'fixed' | 'variable' | 'na'
> extends ChangeBase<DataKey, Option[]> {
  change: 'hybrid'
  options: VariableOptionsPropertyType<Space, DataKey, DataRow, Option, Options, OptionsOf>
  otherOption?: OptionsOf extends 'na' ? Option : never
  onChange?: never
  requestChange: RequestChange<DataKey, Option[]>
  display?: never
}

/**
 * ChartSingleVariable is the type used to define a single select variable.
 *
 * @property type - The variable type (dimension, value, valueOf, metric, etc.).
 * @property change - The change behaviour: static, controlled, internal or hybrid. Type: {@link ChangeStatic} | {@link ChangeControlled} | {@link ChangeSingleInternal} | {@link ChangeSingleHybrid}
 * Extends {@link ChartBaseVariable}.
 */
export interface ChartSingleVariable<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Option extends string,
  Options extends 'static' | 'optionalStatic' | 'withCallback' | 'optionalWithCallback',
  OptionsOf extends 'fixed' | 'variable' | 'na'
> extends ChartBaseVariable<DataKey> {
  type:
    | 'dimension'
    | 'value'
    | 'valueOf'
    | 'metric'
    | 'metricVariants'
    | 'metricTransposed'
    | 'variantValue'
    | TableColumnsChoiceType
  change:
    | ChangeStatic<Space, DataKey, DataRow, Option, Options, OptionsOf>
    | ChangeControlled<Space, DataKey, DataRow, Option, Options, OptionsOf>
    | ChangeSingleInternal<Space, DataKey, DataRow, Option, Options, OptionsOf>
    | ChangeSingleHybrid<Space, DataKey, DataRow, Option, Options, OptionsOf>
}

/**
 * ChartMultipleVariable is the type used to define a multiple select variable.
 *
 * Static is only allowed internally.
 *
 * @property type - The variable type (values, valuesOf, metrics, variantValues).
 * @property change - The change behaviour: static, controlled, internal or hybrid. Type: {@link ChangeStatic} | {@link ChangeControlled} | {@link ChangeMultipleInternal} | {@link ChangeMultipleHybrid}
 * Extends {@link ChartBaseVariable}.
 */
interface ChartMultipleVariable<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Option extends string,
  Options extends 'static' | 'optionalStatic' | 'withCallback' | 'optionalWithCallback',
  OptionsOf extends 'fixed' | 'variable' | 'na'
> extends ChartBaseVariable<DataKey> {
  type: 'values' | 'valuesOf' | 'metrics' | 'variantValues' | TableColumnsChoiceType | TableColumnValuesChoiceType
  change:
    | ChangeStatic<Space, DataKey, DataRow, Option, Options, OptionsOf>
    | ChangeControlled<Space, DataKey, DataRow, Option, Options, OptionsOf>
    | ChangeMultipleInternal<Space, DataKey, DataRow, Option, Options, OptionsOf>
    | ChangeMultipleHybrid<Space, DataKey, DataRow, Option, Options, OptionsOf>
}

/**
 * ChartDimension is the type used to define a single dimension variable.
 *
 * @property type - The variable type, 'dimension'.
 */
export interface ChartDimension<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> extends ChartSingleVariable<Space, DataKey, DataRow, DataKey, 'static', 'na'> {
  type: 'dimension'
}

/**
 * ChartVariableDimensionPropType is the type for a variable dimension property in a dimension value(s) variable.
 *
 * The dimension can be either a static, controlled, internal or hybrid variable.
 */
type ChartVariableDimensionPropType<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = Space extends 'choice' ? DataKey : ChartDimension<Space, DataKey, DataRow>

/**
 * ChartDimensionValue is the type used to define a single dimension value variable.
 *
 * @property type - 'value' when dimension is fixed, 'valueOf' when dimension is variable.
 * @property dimension - The dimension key or variable dimension (when Dimension is 'variable'). When variable: {@link ChartVariableDimensionPropType}
 * @property sort - Optional sort for dimension values. Type: {@link DimensionValuesSortType}
 * @property filter - Optional filter (e.g. valuedMetrics). Type: {@link MetricFilterType}
 * Extends {@link ChartSingleVariable}.
 */
export interface ChartDimensionValue<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Options extends 'static' | 'optionalStatic' | 'withCallback' | 'optionalWithCallback',
  Dimension extends 'fixed' | 'variable'
> extends ChartSingleVariable<Space, DataKey, DataRow, string, Options, Dimension> {
  type: Dimension extends 'fixed' ? 'value' : 'valueOf'
  dimension: Space extends 'choice'
    ? DataKey
    : Dimension extends 'fixed'
    ? DataKey
    : ChartVariableDimensionPropType<Space, DataKey, DataRow>
  sort?: DimensionValuesSortType
  filter?: MetricFilterType
}

/**
 * DimensionValuesSortType is the type used to define how dimension values are sorted (asc, desc, by metrics).
 */
export type DimensionValuesSortType = SortDirection | 'metricsAsc' | 'metricsDesc'

/**
 * TableColumnSortType is the type used to define how table columns are sorted (asc, desc).
 */
export type TableColumnSortType = SortDirection | false

/**
 * ChartDimensionValues is the type used to define a multiple dimension values variable.
 *
 * @property type - 'values' when dimension is fixed, 'valuesOf' when dimension is variable.
 * @property dimension - The dimension key or variable dimension (when Dimension is 'variable'). When variable: {@link ChartVariableDimensionPropType}
 * @property limit - Optional maximum number of dimension values to choose simultaneously.
 * @property sort - Optional sort for dimension values. Type: {@link DimensionValuesSortType} or {@link ChartDataRowSort}
 * @property filter - Optional filter. Type: {@link MetricFilterType} or {@link ChartFilterValues}
 * Extends {@link ChartMultipleVariable}.
 */
export interface ChartDimensionValues<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Options extends 'static' | 'optionalStatic' | 'withCallback' | 'optionalWithCallback',
  Dimension extends 'fixed' | 'variable',
  Sort extends 'with-data' | 'text' = 'with-data'
> extends ChartMultipleVariable<Space, DataKey, DataRow, string, Options, Dimension> {
  type: Dimension extends 'fixed' ? 'values' : 'valuesOf'
  dimension: Space extends 'choice'
    ? DataKey
    : Dimension extends 'fixed'
    ? DataKey
    : ChartVariableDimensionPropType<Space, DataKey, DataRow>
  limit?: number
  sort?: Sort extends 'with-data'
    ? DimensionValuesSortType | ChartDataRowSort<DataKey, DataRow>
    : DimensionValuesSortType
  filter?: Sort extends 'with-data' ? MetricFilterType | ChartFilterValues<DataKey, DataRow> : MetricFilterType
}

/**
 * ChartColumnDimensionValues is the type used to define a multiple column dimension values variable.
 *
 * @property type - 'values' when dimension is fixed, 'valuesOf' when dimension is variable.
 * @property dimension - The dimension key or variable dimension (when Dimension is 'variable'). When variable: {@link ChartVariableDimensionPropType}
 * @property limit - Optional maximum number of dimension values to choose simultaneously.
 * @property sort - Optional sort for dimension values. Type: {@link DimensionValuesSortType} or {@link ChartDataRowSort}
 * @property filter - Optional filter. Type: {@link MetricFilterType} or {@link ChartFilterValues}
 * Extends {@link ChartMultipleVariable}.
 */
export interface ChartColumnDimensionValues<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Options extends 'static' | 'optionalStatic' | 'withCallback' | 'optionalWithCallback',
  Sort extends 'with-data' | 'text' = 'with-data'
> extends ChartMultipleVariable<Space, DataKey, DataRow, string, Options, 'fixed'> {
  type: TableColumnValuesChoiceType
  dimension: Space extends 'choice'
    ? DataKey
    : Dimension extends 'fixed'
    ? DataKey
    : ChartVariableDimensionPropType<Space, DataKey, DataRow>
  limit?: number
  sort?: Sort extends 'with-data'
    ? DimensionValuesSortType | ChartDataRowSort<DataKey, DataRow>
    : DimensionValuesSortType
  filter?: Sort extends 'with-data' ? MetricFilterType | ChartFilterValues<DataKey, DataRow> : MetricFilterType
}

/**
 * ChartVariantValue is the type used to define a single variant value variable.
 *
 * @property type - The variable type, 'variantValue'.
 * @property dimension - The variant dimension id.
 */
export interface ChartVariantValue<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> extends ChartSingleVariable<Space, DataKey, DataRow, string, 'optionalStatic', 'fixed'> {
  type: 'variantValue'
  dimension: DataKey
}

/**
 * ChartVariantValues is the type used to define a multiple variant values variable.
 *
 * @property type - The variable type, 'variantValues'.
 * @property dimension - The variant dimension id.
 * @property sort - Optional sort for variant values.
 * @property filter - Optional filter (e.g. valuedMetrics or custom filter function).
 */
export interface ChartVariantValues<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Sort extends 'with-data' | 'text' = 'with-data'
> extends ChartMultipleVariable<Space, DataKey, DataRow, string, 'optionalStatic', 'fixed'> {
  type: 'variantValues'
  dimension: DataKey
  sort?: Sort extends 'with-data'
    ? DimensionValuesSortType | ChartDataRowSort<DataKey, DataRow>
    : DimensionValuesSortType
  filter?: Sort extends 'with-data' ? MetricFilterType | ChartFilterValues<DataKey, DataRow> : MetricFilterType
}

/**
 * VariantIdOrValue is the type used to define a variant single value.
 * if a variant id is used, then a single controlled variable will be created for selecting one variant value.
 */
export type VariantIdOrValue<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = Space extends 'choice' ? DataKey : DataKey | ChartVariantValue<Space, DataKey, DataRow>

/**
 * VariantIdOrValues is the type used to define a variant single or multiple values.
 * if a variant id is used, then a single controlled variable will be created for selecting one variant value.
 */
export type VariantIdOrValues<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = Space extends 'choice' ? DataKey : DataKey | ChartVariantValues<Space, DataKey, DataRow>

/**
 * TransposeDimensionIdOrValues is the type used to define the dimension(s) used for transposing: either an id or a dimension values variable.
 */
export type TransposeDimensionIdOrValues<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Options extends 'static' | 'optionalStatic' | 'withCallback' | 'optionalWithCallback'
> = Space extends 'choice'
  ? DataKey
  :
      | DataKey
      | ChartDimensionValues<Space, DataKey, DataRow, Options, 'fixed', 'text'>
      | ChartDimensionValues<Space, DataKey, DataRow, Options, 'variable', 'text'>

/**
 * TransposeDimensionIdOrValue is the type used to define the dimension used for transposing: either an id or a dimension value variable.
 */
export type TransposeDimensionIdOrValue<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Options extends 'static' | 'optionalStatic' | 'withCallback' | 'optionalWithCallback'
> = Space extends 'choice'
  ? DataKey
  :
      | DataKey
      | ChartDimensionValue<Space, DataKey, DataRow, Options, 'fixed'>
      | ChartDimensionValue<Space, DataKey, DataRow, Options, 'variable'>

/**
 * ChartMetric is the type used to define a single metric variable.
 *
 * The metric can be part of a variant, whose single value will become a variable.
 * For metric(s) part of a variant, setting the variant property with the id of the variant
 * is allowed just for clarity; the behaviour will be the same as not specifying it (a single internal variable).
 * The ChartVariantValue object can be used to customise the behaviour (e.g. limit selectable variant options).
 *
 * @property type - The variable type, 'metric'.
 * @property variant - Optional. Defines how and which variant value is chosen for the metric(s). Type: {@link VariantIdOrValue}
 * Extends {@link ChartSingleVariable}.
 */
export interface ChartMetric<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> extends ChartSingleVariable<Space, DataKey, DataRow, DataKey, 'static', 'na'> {
  type: 'metric'
  variant?: VariantIdOrValue<Space, DataKey, DataRow>
}

/**
 * ChartMetrics is the type used to define a multiple metrics variable.
 *
 * Any of the metrics can be part of a variant, whose single value will become a variable.
 * For metric(s) part of a variant, setting the variant property with the id of the variant
 * is allowed just for clarity; the behaviour will be the same as not specifying it.
 * The ChartVariantValue object can be used to customise the behaviour (e.g. limit selectable variant options).
 *
 * @property type - The variable type, 'metrics'.
 * @property variant - Optional. Defines how and which variant value is chosen for the metric(s). Type: {@link VariantIdOrValue}
 * @property selected - Optional metrics selected by default. Must be a subset of the change options. Without it, all options are selected (then capped by {@link limit} if set).
 * @property limit - Optional maximum number of metrics that can be selected at once. Also caps the default selection when {@link selected} is omitted.
 * Extends {@link ChartMultipleVariable}.
 */
export interface ChartMetrics<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> extends ChartMultipleVariable<Space, DataKey, DataRow, DataKey, 'static', 'na'> {
  type: 'metrics'
  variant?: VariantIdOrValue<Space, DataKey, DataRow>
  selected?: DataKey[]
  limit?: number
}

export interface ChartTableColumns<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Options extends 'static' | 'optionalStatic' | 'withCallback' | 'optionalWithCallback'
> extends ChartMultipleVariable<Space, DataKey, DataRow, DataKey, Options, 'na'> {
  type: TableColumnsChoiceType
  columns: Array<DataKey | ChartCardTableColumnDef<DataKey, DataRow>>
  /** Column ids selected by default. Must be a subset of the change options. Without it, all columns are selected. */
  selected?: DataKey[]
}

/**
 * ChartMetricVariants is the type used to define a single metric over multiple variant values.
 *
 * More than one variant value (each linked to a metric) can be chosen.
 * The metric can be part of another variant, whose single value will become a variable.
 * The ChartVariantValues object can be used to customise the behaviour (e.g. limit selectable variant options).
 *
 * @property type - The variable type, 'metricVariants'.
 * @property variant - Defines how and which variant values are chosen for the metric. Type: {@link VariantIdOrValues}
 * @property transpose - Optional dimension whose values are used to transpose the metric. Type: {@link TransposeDimensionIdOrValue}
 * @property filter - Optional; whether to filter data based on transposed selection.
 * Extends {@link ChartSingleVariable}.
 */
export interface ChartMetricVariants<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> extends ChartSingleVariable<Space, DataKey, DataRow, DataKey, 'static', 'na'> {
  type: 'metricVariants'
  variant: VariantIdOrValues<Space, DataKey, DataRow>
  transpose?: TransposeDimensionIdOrValue<Space, DataKey, DataRow, 'optionalWithCallback'>
  filter?: boolean
}

/**
 * ChartMetricTransposed is the type used to define a single metric variable transposed across a dimension's values.
 *
 * The transpose property can be used to transpose a single metric across one dimension's values.
 * If the metric is part of a variant, only a single variant value can be selected.
 * Specifying a dimension id creates a multiple internal variable for selecting dimension values.
 * Specifying ChartDimensionValues allows customising the dimension and limiting dimension values.
 * If filter is true, data is filtered by the transposed selection; this affects other metrics' aggregated values.
 *
 * @property type - The variable type, 'metricTransposed'.
 * @property variant - Optional. Defines how and which variant value is chosen for the metric. Type: {@link VariantIdOrValue}
 * @property transpose - The dimension(s) whose values will be used to transpose the metric. Type: {@link TransposeDimensionIdOrValues}
 * @property filter - Optional. Whether to filter the data based on the selection of the values for transposing.
 * Extends {@link ChartSingleVariable}.
 */
export interface ChartMetricTransposed<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> extends ChartSingleVariable<Space, DataKey, DataRow, DataKey, 'static', 'na'> {
  type: 'metricTransposed'
  variant?: VariantIdOrValue<Space, DataKey, DataRow>
  transpose: TransposeDimensionIdOrValues<Space, DataKey, DataRow, 'optionalWithCallback'>
  filter?: boolean
}

/**
 * ChartMetricOrMetrics is the cumulative type used to define a chart metric or metrics variable.
 */
export type ChartMetricOrMetrics<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> =
  | ChartMetric<Space, DataKey, DataRow>
  | ChartMetrics<Space, DataKey, DataRow>
  | ChartMetricVariants<Space, DataKey, DataRow>
  | ChartMetricTransposed<Space, DataKey, DataRow>

/**
 * SingleSelectionValue is the type used to define the value of a single selection (one DataKey or string).
 *
 * Either a single or multiple selection.
 */
export type SingleSelectionValue<DataKey extends string> = DataKey | string

/**
 * MultipleSelectionValue is the type used to define the value of a multiple selection (array of DataKey or string).
 */
export type MultipleSelectionValue<DataKey extends string> = DataKey[] | string[]

/**
 * SelectionValue is the type used to define the value of a selection (single or multiple).
 */
export type SelectionValue<DataKey extends string> = SingleSelectionValue<DataKey> | MultipleSelectionValue<DataKey>

/**
 * ChartSelections is the type used to define the selections for the various variables (fixed or not)
 * defined in the chart options.
 * * The Key is the id of the variable.
 * * The Value is the selected value.
 * > * If the variable is a single select:
 * > > * the value is required.
 * > > * for metrics or dimensions the value is a DataKey.
 * > > * for dimension values the value is a string.
 * > * If the variable is a multiple select:
 * > > * for metrics the value is an array of DataKey
 * > > * for dimension values the value is an array of string.
 */
export type ChartSelections<DataKey extends string> = SelectionValuesMap<DataKey>

/**
 * ChartDimensionValuesVariable is the type used for a dimension values / variant values variable.
 */
export type ChartDimensionValuesVariable<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> =
  | ChartDimensionValues<Space, DataKey, DataRow, 'optionalWithCallback', 'fixed'>
  | ChartDimensionValues<Space, DataKey, DataRow, 'optionalWithCallback', 'variable'>
  | ChartVariantValues<Space, DataKey, DataRow>

/**
 * ChartDimensionValuesNoVariantVariable is the type used for a dimension values variable.
 */
export type ChartDimensionValuesNoVariantVariable<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> =
  | ChartDimensionValues<Space, DataKey, DataRow, 'optionalWithCallback', 'fixed'>
  | ChartDimensionValues<Space, DataKey, DataRow, 'optionalWithCallback', 'variable'>

/**
 * ChartDimensionNoVariantAxis is the type used for a prop that accepts either a dimension id or a dimension values variable.
 */
export type ChartDimensionValuesNoVariantAxis<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> =
  | DataKey
  | ChartDimensionValues<Space, DataKey, DataRow, 'optionalWithCallback', 'fixed'>
  | ChartDimensionValues<Space, DataKey, DataRow, 'optionalWithCallback', 'variable'>

/**
 * ChartDimensionValuesOrMetricVariable is the type used for a prop that accepts dimension values or a single metric variable.
 */
export type ChartDimensionValuesOrMetricVariable<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = ChartDimensionValuesVariable<Space, DataKey, DataRow> | ChartMetric<Space, DataKey, DataRow>

type ChartMetricOrMetricsAxis<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = DataKey | DataKey[] | ChartMetricOrMetrics<Space, DataKey, DataRow>

/**
 * ChartBaseDefinition contains the properties to define the base chart axis. Extended by each of the three types of Charts.
 *
 * @property id - The axis/chart id.
 * @property type - The type of chart.
 * @property layout - Optional layout of the chart, horizontal or vertical. Type: {@link LayoutType}
 * @property otherType - Optional type of the other axis (for Composed Chart). Type: {@link StandardChartType}
 * @property firstAxis - The first axis of the chart. Type: {@link ChartDimensionValuesOrMetricVariable}
 * @property secondAxis - The second axis of the chart. Type: {@link ChartMetricOrMetrics}
 * @property otherSecondAxis - Optional other second axis (for Composed Chart). Type: {@link ChartMetricOrMetrics}
 * @property mainOther - Optional; which second axis is main for composed chart.
 * @property thirdAxis - Optional third axis (Z axis for Scatter Chart). Type: {@link ChartDimensionValuesAxis}
 * @property customProps - Optional custom drawing props for the main chart type. Type: `Omit<{@link DrawingInnerProps}, 'dataKey'>`
 * @property otherCustomProps - Optional custom drawing props for the other chart type (composed).
 * @property choicesTogether - Optional; whether to show choices together in UI.
 */
interface ChartBaseDefinition<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Type extends ChartType = never,
  OtherType extends StandardChartType = never
> {
  type: Type
  layout?: LayoutType
  otherType?: OtherType
  mainOther?: OtherType extends StandardChartType ? boolean : never
  firstAxis: Space extends 'choice' ? DataKey : DataKey | ChartDimensionValuesOrMetricVariable<Space, DataKey, DataRow>
  secondAxis: Space extends 'choice' ? DataKey : ChartMetricOrMetricsAxis<Space, DataKey, DataRow>
  otherSecondAxis?: Space extends 'choice' ? DataKey : ChartMetricOrMetricsAxis<Space, DataKey, DataRow>
  thirdAxis?: Space extends 'choice' ? DataKey : ChartDimensionValuesNoVariantAxis<Space, DataKey, DataRow>
  customProps?: Omit<DrawingInnerProps<Type>, 'dataKey'>
  otherCustomProps?: Omit<DrawingInnerProps<OtherType>, 'dataKey'>
  choicesTogether?: boolean
}

/**
 * ScatterChartAxisProps contains the properties to define the scatter chart axis.
 * @property type - The type of chart, {@link ScatterChartType}.
 * @property firstAxis - The first axis of the chart, a single metric. Type: {@link ChartMetric}
 * @property secondAxis - The second axis of the chart, a single metric. Type: {@link ChartMetric}
 * @property thirdAxis - The third axis of the chart (Z axis), a single dimension values. Type: {@link ChartDimensionValuesNoVariantAxis}
 * > Choosing a partial set of values acts as a filter on the dimension values.
 * Extends {@link ChartBaseDefinition}.
 */
export interface ScatterChartBaseDefinition<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> extends ChartBaseDefinition<Space, DataKey, DataRow, ScatterChartType> {
  otherType?: never
  mainOther?: never
  firstAxis: Space extends 'choice' ? DataKey : DataKey | ChartMetric<Space, DataKey, DataRow>
  secondAxis: Space extends 'choice' ? DataKey : DataKey | ChartMetric<Space, DataKey, DataRow>
  thirdAxis: Space extends 'choice' ? DataKey : DataKey | ChartDimensionValuesNoVariantAxis<Space, DataKey, DataRow>
  otherSecondAxis?: never
}

/**
 * StandardChartAxisProps contains the properties to define the standard chart axis.
 * @property type - The first type of chart, {@link CartesianChartType}.
 * @property firstAxis - The first axis of the chart, a single dimension values. Type: {@link ChartDimensionValuesAxis}
 * > Choosing a partial set of values acts as a filter on the dimension values.
 * @property secondAxis - The second axis of the chart, a single metric or multiple metrics.
 * Extends {@link ChartBaseDefinition}.
 */
export interface CartesianChartBaseDefinition<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Type extends CartesianChartType = CartesianChartType,
  OtherType extends CartesianChartType = CartesianChartType
> extends ChartBaseDefinition<Space, DataKey, DataRow, Type, OtherType> {
  otherType?: OtherType
  firstAxis: Space extends 'choice' ? DataKey : DataKey | ChartDimensionValuesVariable<Space, DataKey, DataRow>
  secondAxis: Space extends 'choice' ? DataKey : ChartMetricOrMetricsAxis<Space, DataKey, DataRow>
  otherSecondAxis?: Space extends 'choice' ? DataKey : ChartMetricOrMetricsAxis<Space, DataKey, DataRow>
  thirdAxis?: never
}

/**
 * StandardChartAxisProps contains the properties to define the standard chart axis.
 * @property type - The first type of chart, {@link CartesianChartType}.
 * @property firstAxis - The first axis of the chart, a single dimension values. Type: {@link ChartDimensionValuesAxis}
 * > Choosing a partial set of values acts as a filter on the dimension values.
 * @property secondAxis - The second axis of the chart, a single metric or multiple metrics.
 * Extends {@link ChartBaseDefinition}.
 */
export interface CartesianPerformanceChartBaseDefinition<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Type extends PerformanceChartType = PerformanceChartType
> extends CartesianChartBaseDefinition<Space, DataKey, DataRow, Type, never> {
  firstAxis: Space extends 'choice'
    ? DataKey
    : DataKey | ChartDimensionValues<Space, DataKey, DataRow, 'optionalWithCallback', 'fixed'>
  secondAxis: Space extends 'choice' ? DataKey : ChartMetricOrMetricsAxis<Space, DataKey, DataRow>
  otherSecondAxis?: never
  thirdAxis?: never
}

/**
 * PolarChartAxisProps contains the properties to define the standard chart axis.
 * @property type - The first type of chart, {@link PolarChartType}.
 * @property firstAxis - The first axis of the chart, a single dimension values. Type: {@link ChartDimensionValuesVariable}
 * > Choosing a partial set of values acts as a filter on the dimension values.
 * @property secondAxis - The second axis of the chart, a single metric or multiple metrics. Type: {@link ChartMetric}
 * Extends {@link ChartBaseDefinition}.
 */
export interface PolarChartBaseDefinition<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> extends ChartBaseDefinition<Space, DataKey, DataRow, PolarChartType> {
  firstAxis: Space extends 'choice' ? DataKey : DataKey | ChartDimensionValuesVariable<Space, DataKey, DataRow>
  secondAxis: Space extends 'choice' ? DataKey : DataKey | ChartMetric<Space, DataKey, DataRow>
  otherSecondAxis?: never
  thirdAxis?: never
}

/**
 * ComposedChartAxisProps contains the properties to define the composed chart axis.
 * @property type - The first type of chart, {@link CartesianChartType}.
 * @property otherType - The second type of chart for the other second axis, {@link CartesianChartType}.
 * @property firstAxis - The first axis of the chart, a single dimension values. Type: {@link ChartDimensionValuesAxis}
 * > Choosing a partial set of values acts as a filter on the dimension values.
 * @property secondAxis - The second axis of the chart, a single metric or multiple metrics.
 * @property otherSecondAxis - The other second axis of the chart. Type: {@link ChartMetricOrMetrics}
 * Drawn as the otherType property.
 * Extends {@link ChartBaseDefinition}.
 */
export interface ComposedChartBaseDefinition<
  Space extends 'choice' | 'props',
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> extends CartesianChartBaseDefinition<Space, DataKey, DataRow, ComposableChartTypes, ComposableChartTypes> {
  otherType: Required<
    CartesianChartBaseDefinition<Space, DataKey, DataRow, ComposableChartTypes, ComposableChartTypes>
  >['otherType']
  otherSecondAxis: Required<
    CartesianChartBaseDefinition<Space, DataKey, DataRow, ComposableChartTypes, ComposableChartTypes>
  >['otherSecondAxis']
  thirdAxis?: never
}

export type ChartCardScatterChartProps<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = ScatterChartBaseDefinition<'props', DataKey, DataRow>

export type ChartCardCartesianChartProps<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = CartesianChartBaseDefinition<'props', DataKey, DataRow>

export type ChartCardCartesianPerformanceChartProps<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = CartesianPerformanceChartBaseDefinition<'props', DataKey, DataRow>

export type ChartCardPolarChartProps<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = PolarChartBaseDefinition<'props', DataKey, DataRow>

export type ChartCardComposedChartProps<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = ComposedChartBaseDefinition<'props', DataKey, DataRow>

/**
 * AxisProps is the type used to define the axis configuration for any supported chart (scatter, cartesian, polar, composed).
 */
export type ChartCardChartProps<
  DataKey extends string = string,
  DataRow extends ChartDataRow<DataKey> = ChartDataRow<DataKey>
> =
  | ChartCardScatterChartProps<DataKey, DataRow>
  | ChartCardCartesianChartProps<DataKey, DataRow>
  | ChartCardPolarChartProps<DataKey, DataRow>
  | ChartCardComposedChartProps<DataKey, DataRow>

export type ChartCardTableColumnsProps<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = ChartTableColumns<'props', DataKey, DataRow, 'optionalWithCallback'>

/**
 * ColumnsProps is the type used to define the columns configuration for a table card.
 */
export type ColumnsProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> =
  | ChartCardTableColumnsProps<DataKey, DataRow>
  | Array<DataKey | ChartCardTableColumnDef<DataKey, DataRow>>

/**
 * ScatterChartDefinition is the type used for scatter chart axis choices (choice space) with view and dimensionAxis.
 */
export type ScatterChartDefinition<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = ScatterChartBaseDefinition<'choice', DataKey, DataRow> & { view: 'cartesian'; dimensionAxis: 'third' }

/**
 * CartesianChartDefinition is the type used for cartesian chart axis choices (choice space) with view and dimensionAxis.
 */
export type CartesianChartDefinition<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = CartesianChartBaseDefinition<'choice', DataKey, DataRow> & { view: 'cartesian'; dimensionAxis: 'first' }

/**
 * CartesianPerformanceChartDefinition is the type used for cartesian performance chart axis choices (choice space) with view and dimensionAxis.
 */
export type CartesianPerformanceChartDefinition<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = CartesianPerformanceChartBaseDefinition<'choice', DataKey, DataRow> & {
  view: 'cartesian'
  dimensionAxis: 'first'
}

/**
 * ComposedChartDefinition is the type used for composed chart axis choices (choice space) with view and dimensionAxis.
 */
export type ComposedChartDefinition<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = ComposedChartBaseDefinition<'choice', DataKey, DataRow> & { view: 'cartesian'; dimensionAxis: 'first' }

/**
 * PolarChartDefinition is the type used for polar chart axis choices (choice space) with view and dimensionAxis.
 */
export type PolarChartDefinition<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = PolarChartBaseDefinition<'choice', DataKey, DataRow> & { view: 'polar'; dimensionAxis: 'first' }

/**
 * ChartCardChartDefinition is the type used for axis choices (choice space) for any chart kind (polar, cartesian, composed, scatter).
 */
export type ChartCardChartDefinition<DataKey extends string, DataRow extends ChartDataRow<DataKey>> =
  | PolarChartDefinition<DataKey, DataRow>
  | CartesianChartDefinition<DataKey, DataRow>
  | CartesianPerformanceChartDefinition<DataKey, DataRow>
  | ComposedChartDefinition<DataKey, DataRow>
  | ScatterChartDefinition<DataKey, DataRow>

/**
 * FirstAxisProps is the type used to extract the first axis type from AxisProps.
 */
export type FirstAxisProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> = ChartCardChartProps<
  DataKey,
  DataRow
>['firstAxis']

/**
 * SecondAxisProps is the type used to extract the second axis type from AxisProps.
 */
export type SecondAxisProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> = ChartCardChartProps<
  DataKey,
  DataRow
>['secondAxis']

/**
 * OtherSecondAxisProps is the type used to extract the other second axis type from AxisProps (composed chart).
 */
export type OtherSecondAxisProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> = ChartCardChartProps<
  DataKey,
  DataRow
>['otherSecondAxis']

/**
 * ThirdAxisProps is the type used to extract the third axis type from AxisProps (scatter Z axis).
 */
export type ThirdAxisProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> = ChartCardChartProps<
  DataKey,
  DataRow
>['thirdAxis']

/**
 * DateRange is the interface used to define the date range filter optionally applied to the data.
 *
 * @property startDate - Start of the date range. Type: Date or (when Space is 'props') string | number.
 * @property endDate - End of the date range. Type: Date or (when Space is 'props') string | number.
 */
export interface DateRange<Space extends 'choice' | 'props'> {
  startDate: Space extends 'choice' ? Date : Date | string | number
  endDate: Space extends 'choice' ? Date : Date | string | number
}

/**
 * DateRangeChoice is the type used for the finalised date range (choice space).
 */
export type DateRangeChoice = DateRange<'choice'>

/**
 * ChartCardClientDataProps — data supplied by the consumer (pre-fetched).
 * ChartCard paginates locally when `customisations.usePagination` is true.
 *
 * @property data - The raw data to be used in the chart. Type: {@link ChartDataRow}[]
 * @property isLoading - Whether the data is currently loading.
 * @property isError - Whether the data is currently in error.
 */
export interface ChartCardClientDataProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  data: DataRow[] | null | undefined
  fetchData?: never
  isLoading: boolean
  isError: boolean
}

/**
 * ChartCardDataDefinitionProps contains the properties needed to define the raw data and how it is used in the chart.
 *
 * @property dimensions - The dimensions of the data. Type: {@link Dimension}[]
 * @property metrics - The metrics of the data. Type: {@link Metric}[]
 */
export interface ChartCardDataDefinitionProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  dimensions: Array<Dimension<'props', DataKey, DataRow>>
  metrics: Array<Metric<DataKey>>
}

/**
 * ChartCardCardBasicProps contains the basic properties for a chart card.
 * @property id - Unique chart card id.
 * @property title - Chart card title.
 * @property t - The function to translate the chart card (either t from useTranslation('chartCard') or {@link ChartCardTFunction}}).
 */
export interface ChartCardCardBasicProps<DataKey extends string> {
  id: DataKey
  title: string
  t?: ChartCardTFunction
}

/**
 * ChartCardTableColumnMeta is the type used to define the chartCard property of meta attribute of the {@link ColumnDef} (from @tanstack/react-table).
 * @property label - The label of the column.
 * @property description - The description of the column.
 * @property color - The color of the column.
 * @property pattern - The line pattern of the column.
 * @property fixedSize - Whether the size of the column is fixed. defaults to false.
 * @property sort - The sort state of the column.
 * @property defaultSortDirection - The default sort direction of the column.
 * Defaults to 'desc' for metrics and 'asc' for dimensions.'
 * @property fixedSort - Whether the sort state is fixed. defaults to false.
 * @property pin - The pinning state of the column.
 * @property fixedPin - Whether the pinning state is fixed. defaults to false.
 * @property colorScale - Display color scale state.
 * true: yes with default color, custom {@link ColumnColorScaleState}). defaults to false.
 * @property fixedColorScale - Whether the color scale state is fixed. defaults to false.
 */
export interface ChartCardTableColumnMeta {
  label?: string
  description?: string
  color?: ThemeColor
  pattern?: LinePattern
  descriptions?: Record<string, BaseElementDescription>
  fixedSize?: boolean
  sort?: TableColumnSortType
  defaultSortDirection?: SortDirection
  fixedSort?: boolean
  pin?: ColumnPinningPosition
  fixedPin?: ColumnPinningPosition | 'no'
  noFiltering?: boolean
  colorScale?: ColumnColorScaleState | boolean | 'no'
  fixedColorScale?: boolean
  align?: CellAlignment
}

export type ChartCardSubType = 'performance'

export type ChartCardType = 'chart' | 'table' | ChartCardSubType

/**
 * ChartCardCardsPropsBase contains the core card definition properties for all cards.
 * @property type - The type of card: 'chart' | 'performance' | 'table'.
 * @property chart - The chart properties. Type: {@link ChartCardChartProps}
 * @property performance - The performance chart properties. Type: {@link ChartCardCartesianPerformanceChartProps}
 * @property columns - The columns of the table. Type: {@link ChartTableColumns}[]
 */
export interface ChartCardCardsPropsBase<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  type: ChartCardType
  chart?: ChartCardChartProps<DataKey, DataRow>
  performance?: ChartCardCartesianPerformanceChartProps<DataKey, DataRow>
  columns?: ColumnsProps<DataKey, DataRow>
}

export interface ChartCardChartCardProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>>
  extends ChartCardCardsPropsBase<DataKey, DataRow> {
  type: 'chart'
  chart: ChartCardChartProps<DataKey, DataRow>
}

export interface ChartCardPerformanceCardProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>>
  extends ChartCardCardsPropsBase<DataKey, DataRow> {
  type: 'performance'
  performance: ChartCardCartesianPerformanceChartProps<DataKey, DataRow>
}

export interface ChartCardTableCardProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>>
  extends ChartCardCardsPropsBase<DataKey, DataRow> {
  type: 'table'
}

/**
 * ChartCardCardsProps contains the core card definition properties for all cards.
 * @property type - The type of card: 'chart' | 'performance' | 'table'.
 * @property chart - The chart properties. Type: {@link ChartCardChartProps}
 * @property performance - The performance chart properties. Type: {@link ChartCardCartesianPerformanceChartProps}
 * @property columns - The columns of the table. Type: {@link ChartTableColumns}[]
 */
export type ChartCardCardsProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> =
  | ChartCardChartCardProps<DataKey, DataRow>
  | ChartCardPerformanceCardProps<DataKey, DataRow>
  | ChartCardTableCardProps<DataKey, DataRow>

/**
 * ChartCardCardProps contains the properties for a chart card.
 * Extends {@link ChartCardCardBasicProps} and {@link ChartCardCardsProps}.
 */
export type ChartCardCardProps<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = ChartCardCardBasicProps<DataKey> & ChartCardCardsProps<DataKey, DataRow>

/**
 * ChartMutableProps adds the selections and dateRange properties,
 * which should be the only mutable properties.
 *
 * @property dateRange - Optional date range (if applicable). Type: {@link DateRange}
 * @property selections - The selections for the various variables in the chart. Type: {@link ChartSelections}
 */
export interface ChartCardMutableProps<Space extends 'choice' | 'props', DataKey extends string> {
  dateRange?: DateRange<Space>
  selections: ChartSelections<DataKey>
}

/**
 * ExpandedToggleProps contains the properties for the toggle to expand or collapse the chart.
 * @property expanded - Whether the chart is expanded.
 * @property toggle - The function to call when the toggle is clicked.
 */
interface ExpandedToggleProps {
  expanded: boolean
  toggle: () => void
}

export interface ChartCardChartActionContext<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  type: 'chart'
  data: DataRow[]
}

export interface ChartCardTableActionContext<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  type: 'table'
  getRows: () => Array<Row<DataRow>>
  getVisibleRows: () => Array<Row<DataRow>>
  getFilteredRows: () => Array<Row<DataRow>>
  getSelectedRows: () => Array<Row<DataRow>>
}

export interface ChartCardTableRowActionContext<DataKey extends string, DataRow extends ChartDataRow<DataKey>>
  extends Omit<ChartCardTableActionContext<DataKey, DataRow>, 'type'> {
  type: 'tableRow'
  row: Row<DataRow>
}

export type ChartCardActionContext<DataKey extends string, DataRow extends ChartDataRow<DataKey>> =
  | ChartCardChartActionContext<DataKey, DataRow>
  | ChartCardTableActionContext<DataKey, DataRow>
  | ChartCardTableRowActionContext<DataKey, DataRow>

export type ChartCardActionOnClick<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Context extends ChartCardActionContext<DataKey, DataRow> = ChartCardActionContext<DataKey, DataRow>
> = (id: string, context: Context) => void

export type ChartCardActionComponent<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Context extends ChartCardActionContext<DataKey, DataRow> = ChartCardActionContext<DataKey, DataRow>
> = ComponentType<{
  id: string
  getContext: () => Context | null
}>

/**
 * ChartCardAction is the type used to define an action for the chart card.
 *
 * If no label or icon are given, the id is used as label.
 *
 * @property id - The action id. Type: string
 * @property label - Optional action label. Type: string | ReactElement
 * @property icon - Optional icon element. Type: ReactElement
 * @property Component - Optional component. Type: {@link ChartCardActionComponent}
 * @property onClick - Callback when the action is triggered. Type: {@link ChartCardActionOnClick}
 */
export type ChartCardAction<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Context extends ChartCardActionContext<DataKey, DataRow> = ChartCardActionContext<DataKey, DataRow>
> =
  | {
      id: string
      label: string | ReactElement
      icon?: ReactElement
      onClick: ChartCardActionOnClick<DataKey, DataRow, Context>
      Component?: never
    }
  | {
      id: string
      label?: string | ReactElement
      icon: ReactElement
      onClick: ChartCardActionOnClick<DataKey, DataRow, Context>
      Component?: never
    }
  | {
      id: string
      label?: never
      icon?: never
      onClick?: never
      Component: ChartCardActionComponent<DataKey, DataRow, Context>
    }

/**
 * AxisCustomDrawing is the type used for custom axis drawing overrides (partial XYAxisProps without ref, dataKey, data).
 */
export type AxisCustomDrawing = Partial<Omit<XYAxisProps, 'ref' | 'dataKey' | 'data'>>

/**
 * AxisCustomDrawingProps is the interface used to pass custom drawing props per axis (first, second, otherSecond).
 *
 * @property firstAxis - Optional custom drawing for the first axis. Type: {@link AxisCustomDrawing}
 * @property secondAxis - Optional custom drawing for the second axis. Type: {@link AxisCustomDrawing}
 * @property otherSecondAxis - Optional custom drawing for the other second axis (composed chart). Type: {@link AxisCustomDrawing}
 */
export interface AxisCustomDrawingProps {
  firstAxis?: AxisCustomDrawing
  secondAxis?: AxisCustomDrawing
  otherSecondAxis?: AxisCustomDrawing
}

/**
 * SizeProps is the interface used to define size constraints for a chart or card (size, width, height, min/max).
 *
 * @property size - Named grid size (small, medium, large, full). Type: {@link ChartCardGridSize}
 * @property width - Optional fixed width.
 * @property height - Optional fixed height.
 * @property minWidth - Optional minimum width.
 * @property minHeight - Optional minimum height.
 * @property maxWidth - Optional maximum width.
 * @property maxHeight - Optional maximum height.
 */
export interface SizeProps {
  size?: ChartCardGridSize
  width?: number
  height?: number
  minWidth?: number
  minHeight?: number
  maxWidth?: number
  maxHeight?: number
}

export type ChartCardTKeys = TFuncKey<'chartCard'>

export type ChartCardTFunction = TFunction<'chartCard'>

/**
 * ChartCardCommonCustomisationsProps is the interface used to define the common UI-related props of a card.
 *
 * @property cardSize - Optional size for the card container. Type: {@link SizeProps}
 * @property downloadTitle - Optional title used for export/download.
 * @property expandedToggle - Optional expand/collapse state and handler. Type: {@link ExpandedToggleProps}
 * @property allowResetCard - Whether to allow resetting selections, defaults to false.
 * @property onSelectionsUpdated - Callback when selections change. Type: {@link ChartOnSelectionsUpdated}
 * @property allowExport - Whether export is enabled, defaults to false. Forced off when remote paging is active (`fetchData` + `usePagination`), since only the current page is loaded.
 * @property allowChangeCardType - Whether changing card type is enabled, defaults to false.
 * @property actions - Optional primary actions. Type: {@link ChartCardAction}[]
 * @property moreMenuActions - Optional actions in the more menu. Type: {@link ChartCardAction}[]
 */
export interface ChartCardCommonCustomisationsProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  cardSize?: SizeProps
  downloadTitle?: string
  expandedToggle?: ExpandedToggleProps
  allowResetCard?: boolean
  onSelectionsUpdated?: ChartOnSelectionsUpdated<DataKey>
  useDebugTools?: boolean
  allowExport?: boolean
  allowChangeCardType?: boolean
  actions?: Array<ChartCardAction<DataKey, DataRow>>
  moreMenuActions?: Array<ChartCardAction<DataKey, DataRow>>
}

/**
 * ChartCardChartCustomisationsProps is the interface used to define the UI-related props of a chart.
 *
 * @property chartSize - Optional size for the chart area. Type: {@link SizeProps}
 * @property axisDrawing - Optional custom axis drawing overrides. Type: {@link AxisCustomDrawingProps}
 * @property chartDrawing - Optional custom chart drawing overriding the dafaults. Type: {@link DefaultDrawingInnerProps}
 * @property drawingColorMaps - Optional custom drawing color maps. Type: {@link DrawingColorMaps}
 * @property allowZooming - Whether zoom is enabled, defaults to false.
 * @property tooltipContent - Optional custom tooltip content. Type: {@link ContentType}
 */
export interface ChartCardChartCustomisationsProps {
  chartSize?: SizeProps
  axisDrawing?: AxisCustomDrawingProps
  chartDrawing?: Partial<DefaultDrawingInnerProps>
  drawingColorMaps?: DrawingColorMaps<true>
  respectColors?: boolean
  allowZooming?: boolean
  tooltipContent?: ContentType<string | number, string>
}

/**
 * ChartCardPerformanceChartCustomisationsProps is the interface used to define the UI-related props of a performance chart.
 *
 * @property chartSize - Optional size for the chart area. Type: {@link SizeProps}
 * @property chartDrawing - Optional custom chart drawing overriding the dafaults. Type: {@link DefaultDrawingInnerProps}
 * @property drawingColorMaps - Optional custom drawing color maps. Type: {@link DrawingColorMaps}
 * @property respectColors - Whether to respect colors defined in the metrics and dimensions, defaults to false.
 * @property hideChart - Whether to hide the chart, defaults to false.
 * @property hideTotalValue - Whether to hide the total value, defaults to false.
 */
export interface ChartCardPerformanceChartCustomisationsProps {
  chartSize?: SizeProps
  chartDrawing?: Partial<DefaultDrawingInnerProps>
  drawingColorMaps?: DrawingColorMaps<true>
  respectColors?: boolean
  hideChart?: boolean
  hideTotalValue?: boolean
}

/**
 * Query sent to {@link ChartCardFetchData}.
 * For charts (and tables without pagination), page fields are omitted — return the full result set.
 * For tables with `customisations.usePagination`, includes page index/size for remote paging.
 */
export interface ChartCardFetchQuery {
  pageIndex?: number
  pageSize?: number
  sorting: SortingState
  /**
   * Visible dimension column ids — the requested grain. Only set for tables with a column picker.
   * Changing this set refetches; metric column changes do not, because every metric offered by the
   * picker is expected to be in the fetched payload already.
   */
  dimensions?: string[]
}

/**
 * Result of a {@link ChartCardFetchData} call.
 *
 * @property data - Rows for this response (one page when remotely paginated, else the full set).
 * @property totalRows - Total matching rows across all pages when paginated (drives page count). For non-paginated fetches, typically `data.length`.
 * @property totals - Metric totals and ranges for the result set (table footer / color scales). Type: {@link TotalsAndRangeData}
 */
export interface ChartCardFetchResult<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  data: DataRow[]
  totalRows: number
  totals: TotalsAndRangeData<DataKey>
}

/**
 * Consumer-provided fetch for ChartCard-owned data loading (charts and tables).
 * Invoked when the query changes (selections/dateRange/`fetchResetKey` reset; for tables with
 * pagination, also page/sort; for tables with a column picker, also the visible dimension set).
 * ChartCard drives skeleton / error UI from the promise (`isLoading` / `isError`).
 */
export type ChartCardFetchData<DataKey extends string, DataRow extends ChartDataRow<DataKey>> = (
  query: ChartCardFetchQuery
) => Promise<ChartCardFetchResult<DataKey, DataRow>>

/**
 * ChartCardFetchDataProps — ChartCard fetches data via `fetchData` (charts and tables).
 * Does not imply pagination; `customisations.usePagination` enables remote paging for tables only.
 *
 * @property fetchData - Fetches rows for the current query.
 * @property initialPageSize - Initial page size when table server fetch with pagination is on. Defaults to 10.
 * @property fetchResetKey - Optional external reset signal (deep-compared). When it changes, paginated
 *   server fetches reset to page 0 and refetch. Use for query inputs ChartCard does not own via
 *   `mutable` (e.g. dashboard/global filters). `mutable.dateRange` and `mutable.selections` already
 *   participate in the same reset.
 */
export interface ChartCardFetchDataProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  fetchData: ChartCardFetchData<DataKey, DataRow>
  initialPageSize?: number
  fetchResetKey?: unknown
  data?: never
  isLoading?: never
  isError?: never
}

/**
 * ChartCardDataProps is a presence-based union:
 * - {@link ChartCardClientDataProps}: consumer supplies `data` / `isLoading` / `isError`.
 * - {@link ChartCardFetchDataProps}: ChartCard calls `fetchData`.
 */
export type ChartCardDataProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> =
  | ChartCardClientDataProps<DataKey, DataRow>
  | ChartCardFetchDataProps<DataKey, DataRow>

/**
 * Type guard for {@link ChartCardFetchDataProps}.
 */
export const isChartCardFetchDataProps = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  data: ChartCardDataProps<DataKey, DataRow>
): data is ChartCardFetchDataProps<DataKey, DataRow> => data.fetchData != null

/**
 * Type guard for {@link ChartCardClientDataProps}.
 */
export const isChartCardClientDataProps = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  data: ChartCardDataProps<DataKey, DataRow>
): data is ChartCardClientDataProps<DataKey, DataRow> => data.fetchData == null

/**
 * ChartCardTableCustomisationsProps is the interface used to define the UI-related props of a table.
 *
 * @property tableSize - Optional size for the table area. Type: {@link SizeProps}
 * @property usePagination - Whether to use pagination, defaults to false. Works with client data (local paging) or {@link ChartCardFetchDataProps} (remote paging).
 * @property useFuzzySearch - Whether to use fuzzy search, defaults to false. Disabled when remote paging is active (`fetchData` + `usePagination`).
 * @property useDefaultSorting - Whether to use default sorting (dimensions, ascending), defaults to false.
 * @property useDefaultPinning - Whether to auto-pin dimension columns on initial render (up to three total across left and right; defaults to left), defaults to true. Skipped when any column already has an explicit `pin` / `fixedPin` of `left` or `right`. Manual pinning via the column menu is unrestricted.
 * @property useColumnsDecorations - Whether to use columns decorations, defaults to false.
 * @property useBodyScrollDecoration - Whether to use body scroll decoration, defaults to false.
 * @property useOverscrollContain - Whether to use overscroll-behavior: 'contain' on the Table Container, defaults to false.
 * @property allowToggleUsePagination - Whether to allow toggling use pagination, defaults to false. Forced off when remote paging is active.
 * @property allowRowsSelection - Whether rows selection is enabled, defaults to false.
 * @property allowColumnsSelection - Whether columns selection is enabled, defaults to false.
 * @property allowColumnFiltering - Whether column filtering is enabled, defaults to false. Under remote paging, local TanStack filtering stays off; use {@link onOpenColumnFilter} to open an external filter UI.
 * @property allowDateFiltering - Whether date dimension columns show a Filter menu item, defaults to false. Only supported with client-supplied data; forced off under server fetch (dashboards use a page-level date range instead).
 * @property allowColumnResizing - Whether column resizing is enabled, defaults to false.
 * @property allowColumnAutoResizing - Whether column auto resizing is enabled, defaults to false.
 * @property allowColumnPinning - Whether column pinning is enabled, defaults to false.
 * @property allowColumnReordering - Whether column reordering is enabled, defaults to false.
 * @property enableNullDimensionValue - Whether null dimension values are kept, defaults to true. Tables render them as '-' with a "No data" tooltip (cell data stays null); charts collect them as a selectable option and draw them as a category or slice labelled '-' (chart rows carry NULL_DIMENSION_VALUE_OPTION, since Recharts discards null categories). When false they are dropped from chart options and fall back to the "others" bucket.
 * @property onOpenColumnFilter - When set (typically with remote paging), the column Filter menu item calls this instead of the local multi-select filter UI.
 */
export interface ChartCardTableCustomisationsProps {
  tableSize?: SizeProps
  usePagination?: boolean
  useFuzzySearch?: boolean
  useDefaultSorting?: boolean
  useDefaultPinning?: boolean
  useColumnsDecorations?: boolean
  useBodyScrollDecoration?: boolean
  useOverscrollContain?: boolean
  allowToggleUsePagination?: boolean
  allowRowsSelection?: boolean
  allowMultipleColumnsSorting?: boolean
  allowColumnsSelection?: boolean
  allowColumnFiltering?: boolean
  allowDateFiltering?: boolean
  allowColumnResizing?: boolean
  allowColumnAutoResizing?: boolean
  allowColumnPinning?: boolean
  allowColumnReordering?: boolean
  enableNullDimensionValue?: boolean
  onOpenColumnFilter?: (columnId: string) => void
}

/**
 * ChartCardCustomisationsProps is the interface used to define the UI-related props of a chart card.
 * Extends {@link ChartCardCommonCustomisationsProps}, {@link ChartCardChartCustomisationsProps} and {@link ChartCardTableCustomisationsProps}.
 */
export interface ChartCardCustomisationsProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>>
  extends ChartCardCommonCustomisationsProps<DataKey, DataRow>,
    ChartCardChartCustomisationsProps,
    ChartCardPerformanceChartCustomisationsProps,
    ChartCardTableCustomisationsProps {}

/**
 * Shared ChartCard props excluding the data source pair.
 */
interface ChartCardPropsBase<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  card: ChartCardCardProps<DataKey, DataRow>
  dataDefinition: ChartCardDataDefinitionProps<DataKey, DataRow>
  mutable: ChartCardMutableProps<'props', DataKey>
  customisations?: ChartCardCustomisationsProps<DataKey, DataRow>
}

/**
 * ChartCard with consumer-supplied data. `dataCompare`, when set, must also be supplied data.
 */
export type ChartCardClientSourceProps<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = ChartCardPropsBase<DataKey, DataRow> & {
  data: ChartCardClientDataProps<DataKey, DataRow>
  dataCompare?: ChartCardClientDataProps<DataKey, DataRow>
}

/**
 * ChartCard that fetches data. `dataCompare`, when set, must also use `fetchData`.
 */
export type ChartCardFetchSourceProps<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = ChartCardPropsBase<DataKey, DataRow> & {
  data: ChartCardFetchDataProps<DataKey, DataRow>
  dataCompare?: ChartCardFetchDataProps<DataKey, DataRow>
}

/**
 * ChartCardProps — `data` / `dataCompare` must use the same source shape (both supplied, or both `fetchData`).
 * Misaligned combinations are a type error at the call site.
 */
export type ChartCardProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> =
  | ChartCardClientSourceProps<DataKey, DataRow>
  | ChartCardFetchSourceProps<DataKey, DataRow>

/**
 * Narrows to {@link ChartCardFetchSourceProps} (both `data` and optional `dataCompare` use fetch).
 */
export const isChartCardFetchSourceProps = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  props: ChartCardProps<DataKey, DataRow>
): props is ChartCardFetchSourceProps<DataKey, DataRow> => isChartCardFetchDataProps(props.data)

/**
 * Narrows to {@link ChartCardClientSourceProps} (both `data` and optional `dataCompare` are supplied).
 */
export const isChartCardClientSourceProps = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  props: ChartCardProps<DataKey, DataRow>
): props is ChartCardClientSourceProps<DataKey, DataRow> => isChartCardClientDataProps(props.data)

/**
 * ChartCard with client-side (pre-fetched) data only.
 * Use when the consumer always provides the full dataset (e.g. copilot mappers, most stories).
 */
export type ChartCardClientProps<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> = ChartCardClientSourceProps<DataKey, DataRow>

/**
 * ChartCardDefaultsAngledLabels is the interface used for default settings for angled axis labels (fallback length, space, angle, wrap).
 *
 * @property fallbackLength - Fallback label length in pixels.
 * @property fallbackSpace - Fallback space in pixels.
 * @property angle - Label angle in degrees.
 * @property wrapLines - Number of lines for wrapping.
 * @property maxSpace - Maximum space in pixels (0 means no max).
 */
export interface ChartCardDefaultsAngledLabels {
  fallbackLength: number
  fallbackSpace: number
  angle: number
  wrapLines: number
  maxSpace: number
}

/**
 * ChartCardDefaultsAxisSpace is the interface used for default axis spacing (vertical label offset, x/y offsets).
 *
 * @property verticalLabelOffset - Vertical label offset in pixels.
 * @property xDY - X axis dY in pixels.
 * @property xStandardHorizontal - X standard horizontal spacing in pixels.
 * @property xStandardVertical - X standard vertical spacing in pixels.
 * @property xScatter - X scatter spacing in pixels.
 * @property yHorizontal - Y horizontal spacing in pixels.
 * @property yVertical - Y vertical spacing in pixels.
 */
export interface ChartCardDefaultsAxisSpace {
  verticalLabelOffset: number
  horizontalLabelOffset: number
  xDY: number
  xStandardHorizontal: number
  xStandardVertical: number
  xScatter: number
  yHorizontal: number
  yVertical: number
}

/**
 * ChartCardDefaultsExport is the interface used for default export PNG/SVG settings.
 *
 * @property labelGap - Label gap in pixels.
 * @property itemsGap - Items gap in pixels.
 * @property groupedItemsGap - Grouped items gap in pixels.
 * @property headerBottomPadding - Header bottom padding in pixels.
 * @property fallbackFontFamilySVG - Fallback font family for SVG export.
 * @property fallbackFontFamilyPNG - Fallback font family for PNG export.
 * @property scalePNG - Scale factor for PNG export.
 */
export interface ChartCardDefaultsExport {
  labelGap: number
  itemsGap: number
  groupedItemsGap: number
  headerBottomPadding: number
  fallbackFontFamilySVG: string
  fallbackFontFamilyPNG: string
  scalePNG: number
}

/**
 * HoverablePieShapeProps is the type used for pie/donut shape props with optional hover offsets.
 *
 * @property outerRadiusOffset - Optional outer radius offset for hover. Type: {@link PieProps}['outerRadius']
 * @property innerRadiusOffset - Optional inner radius offset for hover. Type: {@link PieProps}['innerRadius']
 * @property fillShadeOffset - Optional fill shade offset for hover.
 * @property strokeShadeOffset - Optional stroke shade offset for hover.
 * Extends Pick of {@link PieProps} (outerRadius, innerRadius, fill, etc.).
 */
export type HoverablePieShapeProps = Pick<
  PieProps,
  'outerRadius' | 'innerRadius' | 'fill' | 'fillOpacity' | 'stroke' | 'strokeWidth' | 'strokeOpacity' | 'cornerRadius'
> & {
  outerRadiusOffset?: PieProps['outerRadius']
  innerRadiusOffset?: PieProps['innerRadius']
  fillShadeOffset?: number
  strokeShadeOffset?: number
}

/**
 * HoverablePieProps is the type used for pie/donut chart props with hover styling.
 *
 * @property highlightProps - Optional props applied to the highlight portion of the active sector. Type: {@link HoverablePieShapeProps}
 * @property activeProps - Optional props applied to the active sector. Type: {@link HoverablePieShapeProps}
 * @property inactiveProps - Optional props applied to the inactive sectors. Type: {@link HoverablePieShapeProps}
 * @property colorMap - Optional color map for the pie chart.
 * Extends {@link PieProps}.
 */
export type HoverablePieDrawingProps = PieProps & {
  highlightProps?: HoverablePieShapeProps
  activeProps?: HoverablePieShapeProps
  inactiveProps?: HoverablePieShapeProps
  colorMap?: Record<string, string>
}

/**
 * CustomScatterProps is the type used for scatter chart props with optional color map.
 *
 * @property colorMap - Optional color map for the scatter chart.
 * Extends {@link ScatterProps}.
 */
export type CustomScatterDrawingProps = ScatterProps & {
  colorMap?: Record<string, string>
  dataKey: string
}

/**
 * DrawingInnerProps is the mapped type used to get the inner drawing props (Bar, Line, Area, Pie, Scatter) for a given ChartType.
 */
export type DrawingInnerProps<Type extends ChartType> = Type extends 'bar'
  ? Omit<BarProps, 'ref'>
  : Type extends 'line'
  ? Omit<LineProps, 'ref'>
  : Type extends 'gradientLine'
  ? Omit<AreaProps, 'ref'>
  : Type extends 'area'
  ? Omit<AreaProps, 'ref'>
  : Type extends 'pie'
  ? Omit<HoverablePieDrawingProps, 'ref'>
  : Type extends 'donut'
  ? Omit<PieProps, 'ref'>
  : Type extends 'scatter'
  ? Omit<CustomScatterDrawingProps, 'ref'>
  : never

/**
 * DrawingInnerProps is the mapped type used to get the inner drawing props (Bar, Line, Area, Pie, Scatter) for a given ChartType.
 */
export type SpecialDrawingInnerProps<Type extends SpecialDrawingChartType> = Type extends 'gradientLine'
  ? { stops: Array<{ offset: string; stopOpacity: number }> }
  : never

/**
 * DefaultDrawingInnerProps is the type used for a map of ChartType to drawing inner props (without dataKey).
 */
export type DefaultDrawingInnerProps = { [K in ChartType]: Omit<DrawingInnerProps<K>, 'dataKey'> }

/**
 * SpecialDrawingInnerProps is the type used for a map of ChartType to special drawing inner props (without dataKey).
 */
export type DefaultSpecialDrawingInnerProps = { [K in SpecialDrawingChartType]: SpecialDrawingInnerProps<K> }

/**
 * RechartsChartProps is the mapped type used to get the Recharts chart container props for a given RechartsChartType.
 */
export type RechartsChartProps<Type extends RechartsChartType> = Type extends 'bar'
  ? Omit<RechartsBarChartProps, 'ref'>
  : Type extends 'line'
  ? Omit<RechartsLineChartProps, 'ref'>
  : Type extends 'gradientLine'
  ? Omit<RechartsAreaChartProps, 'ref'>
  : Type extends 'area'
  ? Omit<RechartsAreaChartProps, 'ref'>
  : Type extends 'composed'
  ? Omit<RechartsComposedChartProps, 'ref'>
  : Type extends 'pie'
  ? Omit<RechartsPieChartProps, 'ref'>
  : Type extends 'donut'
  ? Omit<RechartsPieChartProps, 'ref'>
  : Type extends 'scatter'
  ? Omit<RechartsScatterChartProps, 'ref'>
  : never

export type DrawingColorMap<Optional extends boolean = false> = Record<
  ChartType,
  Optional extends true ? DrawingColorKey | undefined : DrawingColorKey
>

export interface DrawingColorMaps<Optional extends boolean = false> {
  primary: Optional extends true ? Partial<DrawingColorMap<true>> | undefined : DrawingColorMap
  secondary: Optional extends true ? Partial<DrawingColorMap<true>> | undefined : DrawingColorMap<true>
}

/**
 * DefaultRechartsChartProps is the type used for a map of RechartsChartType to Recharts chart props.
 */
export type DefaultRechartsChartProps = { [K in RechartsChartType]: RechartsChartProps<K> }

/**
 * ChartCardDefaultsFormat is the interface used for default format options (money, number, percentage) used in the chart.
 *
 * @property money - Default format for money values. Type: `Omit<{@link FormatToMoneyProps}, 'value'>`
 * @property number - Default format for number values. Type: {@link FormatToNumberProps}
 * @property percentage - Default format for percentage values. Type: `Omit<{@link FormatToPercentProps}, 'value'>`
 */
export interface ChartCardDefaultsFormat {
  money: Omit<FormatToMoneyProps, 'value'>
  number: FormatToNumberProps
  percentage: Omit<FormatToPercentProps, 'value'>
}

/**
 * Margin is the type used for a margin.
 */
export interface Margin {
  top: number
  right: number
  bottom: number
  left: number
}

/**
 * Padding is the type used for a padding.
 */
export interface Padding {
  top: number
  right: number
  bottom: number
  left: number
}

/**
 * ChartCardDefaults is the interface used to define all default configuration for the chart card.
 *
 * @property chartMargin - Default chart margin. Type: {@link Margin}
 * @property chartViewMargin - Margin per view type (cartesian, polar). Uses {@link ViewType} and {@link Margin}.
 * @property fonts - Font settings for title, axis labels, axis ticks, export. Uses {@link FontProps} for each key.
 * @property chart - Default Recharts chart props per chart type. Type: {@link DefaultRechartsChartProps}
 * @property axis - Axis defaults (angled labels, space, lines, format). Uses {@link ChartCardDefaultsAngledLabels}, {@link ChartCardDefaultsAxisSpace}, {@link ChartCardDefaultsFormat}, and axis line props from {@link XAxisProps} / {@link YAxisProps}.
 * @property drawing - Default drawing props per chart type. Type: {@link DefaultDrawingInnerProps}
 * @property zoomOverlay - Zoom overlay styling (stroke, fill, opacity, etc.). Uses {@link ThemeColor} for stroke and fill.
 * @property tooltip - Tooltip format and out-of-zoom opacity. Uses {@link ChartCardDefaultsFormat} for format.
 * @property others - Other defaults (nonActiveFillOpacity, tooltipAnimationDuration).
 * @property export - Export defaults (gaps, fonts, scale). Type: {@link ChartCardDefaultsExport}
 */
export interface ChartCardDefaults {
  card: {
    padding: 19
    gap: 24
    cardsFixedColumns: boolean
  }
  chartMargin: Margin
  tableMargin: Margin
  chartViewMargin: Partial<Record<ViewType, Margin>>
  cardChartSizes: Record<
    ChartCardSubType,
    {
      height: number
      minWidth: number
    }
  >
  fonts: {
    title: FontProps
    axisLabels: FontProps
    axisTicks: FontProps
    exportLegendLabels: FontProps
    exportLegendChips: FontProps
  }
  chart: DefaultRechartsChartProps
  axis: {
    angledLabels: ChartCardDefaultsAngledLabels
    space: ChartCardDefaultsAxisSpace
    xLine: XAxisProps['axisLine']
    yLine: YAxisProps['axisLine']
    format: ChartCardDefaultsFormat
  }
  table: {
    height: {
      title: number
      pagination: number
    }
    padding: Partial<Record<TableSection, Padding>>
    format: ChartCardDefaultsFormat
    defaultColumn: Pick<ColumnDef<RowData>, 'minSize' | 'maxSize' | 'header' | 'cell'>
    hoverBackgroundColor: Partial<Record<TableSection, ThemeColor>>
    backgroundColor: Partial<Record<TableSection, ThemeColor>>
    resizeIconSize: number
    allowResizingShadow: Partial<Record<TableSection, string | string[]>>
    endPinnedShadow: Partial<Record<TableSection, Record<Exclude<ColumnPinningPosition, false>, string | string[]>>>
    shadows: Partial<Record<TableSection, string | string[]>>
    colorScale: {
      min: number
      max: number
    }
  }
  drawing: DefaultDrawingInnerProps
  specialDrawing: DefaultSpecialDrawingInnerProps
  cardDrawing: Partial<Record<ChartCardType, Partial<DefaultDrawingInnerProps>>>
  drawingColorMaps: DrawingColorMaps
  zoomOverlay: {
    stroke: ThemeColor
    strokeWidth: number
    strokeOpacity: number
    fillOpacity: number
    fill: ThemeColor
    minChangeFactor: number
  }
  tooltip: {
    format: ChartCardDefaultsFormat
    outOfZoomOpacity: number
  }
  others: {
    nonActiveFillOpacity: number
    tooltipAnimationDuration: number
  }
  export: ChartCardDefaultsExport
}
